import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { decksApi, tournamentsApi } from '../services/api';
import { Deck, DeckCreate, DeckUpdate, Tournament, DeckPreviewResponse } from '../types';

const DeckEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [deck, setDeck] = useState<Deck | null>(null);
  const [formData, setFormData] = useState<DeckCreate | DeckUpdate>({
    name: '',
    description: '',
    decklist_text: '',
  });
  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTournamentId, setSelectedTournamentId] = useState<string>('');
  const [preview, setPreview] = useState<DeckPreviewResponse | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importUrl, setImportUrl] = useState('');
  const [importing, setImporting] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    if (isEditMode) {
      fetchDeck();
    }
    fetchTournaments();
  }, [id]);

  const fetchDeck = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const response = await decksApi.getById(id);
      const deckData = response.data;
      setDeck(deckData);
      setFormData({
        name: deckData.name,
        description: deckData.description || '',
        decklist_text: deckData.decklist_text,
      });
    } catch (err: any) {
      console.error('Failed to fetch deck:', err);
      setError('Failed to load deck. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchTournaments = async () => {
    try {
      const response = await tournamentsApi.getAll();
      setTournaments(response.data);
    } catch (err) {
      console.error('Failed to fetch tournaments:', err);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      if (isEditMode && id) {
        const updateData: DeckUpdate = {
          name: formData.name || undefined,
          description: formData.description || undefined,
          decklist_text: formData.decklist_text || undefined,
        };
        await decksApi.update(id, updateData);
      } else {
        const createData: DeckCreate = {
          name: formData.name!,
          description: formData.description,
          decklist_text: formData.decklist_text!,
        };
        const response = await decksApi.create(createData);
        navigate(`/deck-editor/${response.data.id}`);
        return;
      }

      await fetchDeck();
      alert('Deck saved successfully!');
    } catch (err: any) {
      console.error('Failed to save deck:', err);
      setError(err.response?.data?.detail || 'Failed to save deck. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    if (!selectedTournamentId || !id) return;

    try {
      setPreviewLoading(true);
      const response = await decksApi.preview(id, { tournament_id: selectedTournamentId });
      setPreview(response.data);
    } catch (err: any) {
      console.error('Failed to preview deck:', err);
      alert('Failed to preview deck. Please try again.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleImport = async () => {
    if (!importUrl.trim()) {
      alert('Please enter a URL');
      return;
    }

    try {
      setImporting(true);
      const response = await decksApi.importFromUrl({ url: importUrl });
      navigate(`/deck-editor/${response.data.deck.id}`);
    } catch (err: any) {
      console.error('Failed to import deck:', err);
      alert(err.response?.data?.detail || 'Failed to import deck. Please try again.');
    } finally {
      setImporting(false);
      setShowImportModal(false);
      setImportUrl('');
    }
  };

  const handleSync = async () => {
    if (!id || !deck?.source_url) return;

    if (!window.confirm('This will overwrite your current deck with the latest version from the source. Continue?')) {
      return;
    }

    try {
      setSyncing(true);
      const response = await decksApi.syncFromSource(id);
      setDeck(response.data);
      setFormData({
        name: response.data.name,
        description: response.data.description || '',
        decklist_text: response.data.decklist_text,
      });
      setShowSyncModal(false);
      alert('Deck synced successfully!');
    } catch (err: any) {
      console.error('Failed to sync deck:', err);
      alert(err.response?.data?.detail || 'Failed to sync deck. Please try again.');
    } finally {
      setSyncing(false);
    }
  };

  const formatPrice = (price: number | null | undefined) => {
    if (price === null || price === undefined) return 'N/A';
    return `€${price.toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading deck...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <button
            onClick={() => navigate('/my-decks')}
            className="text-blue-600 hover:text-blue-800 mb-4 inline-flex items-center"
          >
            ← Back to My Decks
          </button>
          <h1 className="text-3xl font-bold text-gray-900">
            {isEditMode ? 'Edit Deck' : 'Create New Deck'}
          </h1>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
          {/* Import/Sync Actions */}
          <div className="flex gap-2">
            {!isEditMode && (
              <button
                onClick={() => setShowImportModal(true)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Import from URL
              </button>
            )}
            {isEditMode && deck?.source_url && (
              <button
                onClick={() => setShowSyncModal(true)}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                Sync from Source
              </button>
            )}
          </div>

          {/* Form Fields */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Deck Name *
            </label>
            <input
              type="text"
              value={formData.name || ''}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., My Budget Commander"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              placeholder="Optional description..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Decklist *
            </label>
            <textarea
              value={formData.decklist_text || ''}
              onChange={(e) => setFormData({ ...formData, decklist_text: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
              rows={20}
              placeholder="Paste your decklist here (one card per line, format: quantity cardname)&#10;&#10;Example:&#10;1 Sol Ring&#10;4 Lightning Bolt"
              required
            />
            <p className="text-sm text-gray-500 mt-1">
              Format: One card per line, starting with quantity (e.g., "4 Lightning Bolt")
            </p>
          </div>

          {/* Tournament Preview Section */}
          {isEditMode && id && (
            <div className="border-t pt-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Price Preview</h2>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Tournament
                </label>
                <select
                  value={selectedTournamentId}
                  onChange={(e) => {
                    setSelectedTournamentId(e.target.value);
                    setPreview(null);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">-- Select a tournament --</option>
                  {tournaments.map((tournament) => (
                    <option key={tournament.id} value={tournament.id}>
                      {tournament.name}
                    </option>
                  ))}
                </select>
              </div>
              {selectedTournamentId && (
                <button
                  onClick={handlePreview}
                  disabled={previewLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {previewLoading ? 'Loading...' : 'Preview Price'}
                </button>
              )}

              {preview && (
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-semibold text-gray-800 mb-2">Preview Results</h3>
                  <div className="space-y-2 mb-6">
                    <p>
                      <span className="font-medium">Total Cost:</span> €{preview.total_cost.toFixed(2)}
                    </p>
                    <p>
                      <span className="font-medium">Within Budget:</span>{' '}
                      <span className={preview.within_budget ? 'text-green-600' : 'text-red-600'}>
                        {preview.within_budget ? 'Yes' : 'No'}
                      </span>
                    </p>
                    <p>
                      <span className="font-medium">Valid:</span>{' '}
                      <span className={preview.is_valid ? 'text-green-600' : 'text-red-600'}>
                        {preview.is_valid ? 'Yes' : 'No'}
                      </span>
                    </p>
                    {preview.violations.length > 0 && (
                      <div>
                        <span className="font-medium">Violations:</span>
                        <ul className="list-disc list-inside text-red-600">
                          {preview.violations.map((violation, idx) => (
                            <li key={idx}>{violation}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Card Breakdown Table */}
                  {preview.deck_breakdown && preview.deck_breakdown.length > 0 && (
                    <div className="mt-6">
                      <h4 className="font-semibold text-gray-800 mb-4">Card Breakdown</h4>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Card Name
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Set
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Qty
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Price
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Total
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {preview.deck_breakdown.map((card, index) => (
                              <tr key={index}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  {card.card_name}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {card.set_code || 'N/A'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {card.quantity}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {formatPrice(card.price)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {formatPrice(card.total_price)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Save Button */}
          <div className="flex gap-4">
            <button
              onClick={handleSave}
              disabled={saving || !formData.name || !formData.decklist_text}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Deck'}
            </button>
            <button
              onClick={() => navigate('/my-decks')}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowImportModal(false)}></div>
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Import Deck from URL</h2>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Deck URL (Archidekt or Moxfield)
                </label>
                <input
                  type="text"
                  value={importUrl}
                  onChange={(e) => setImportUrl(e.target.value)}
                  placeholder="https://archidekt.com/decks/..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex gap-4">
                <button
                  onClick={handleImport}
                  disabled={importing || !importUrl.trim()}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  {importing ? 'Importing...' : 'Import'}
                </button>
                <button
                  onClick={() => {
                    setShowImportModal(false);
                    setImportUrl('');
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sync Modal */}
      {showSyncModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowSyncModal(false)}></div>
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Sync Deck from Source</h2>
              <p className="text-gray-600 mb-4">
                This will overwrite your current deck with the latest version from {deck?.source_type}. 
                All current changes will be lost.
              </p>
              <div className="flex gap-4">
                <button
                  onClick={handleSync}
                  disabled={syncing}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
                >
                  {syncing ? 'Syncing...' : 'Sync Deck'}
                </button>
                <button
                  onClick={() => setShowSyncModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeckEditor;


